import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import type { BlockOf, NewRsvp, Site } from '../model/types';
import { Icon } from '../components/Icon';
import { errorMessage } from '../lib/util';
import { siteClass, siteStyle } from './theme';
import { RsvpHead } from './Blocks';

export type SubmitRsvp = (r: NewRsvp) => Promise<void>;

interface Props {
  site: Site;
  block: BlockOf<'rsvp'>;
  container: HTMLElement;
  /** Saves the reply. Leave out for previews, where replies aren't saved. */
  submit?: SubmitRsvp;
  onClose: () => void;
}

interface Done { first: string; yes: boolean; adults: number; kids: number }

/** The reply form, shown over the website in a pop-up. */
export function RsvpDialog({ site: s, block: b, container, submit, onClose }: Props) {
  const [box, setBox] = useState({ top: container.scrollTop, height: container.clientHeight });
  const [done, setDone] = useState<Done | null>(null);
  const [round, setRound] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(typeof document !== 'undefined' ? document.activeElement : null);

  // Cover exactly the visible part of the scrolling website, and stop it scrolling underneath
  useLayoutEffect(() => {
    container.classList.add('rsvp-open');
    const fit = () => setBox({ top: container.scrollTop, height: container.clientHeight });
    fit();
    window.addEventListener('resize', fit);
    return () => { container.classList.remove('rsvp-open'); window.removeEventListener('resize', fit); };
  }, [container]);

  useEffect(() => {
    const ret = opener.current;
    return () => { if (ret instanceof HTMLElement && document.contains(ret)) ret.focus({ preventScroll: true }); };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); onClose(); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const f = Array.from(cardRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, select, textarea')).filter((n) => n.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    const el = cardRef.current?.querySelector<HTMLElement>(done ? '.rsvp-done .s-btn' : 'input[name="name"]');
    el?.focus({ preventScroll: true });
  }, [done, round]);

  const h = s.header;
  const party = done && done.yes
    ? [`${done.adults} ${done.adults === 1 ? 'adult' : 'adults'}`, done.kids ? `${done.kids} ${done.kids === 1 ? 'child' : 'children'}` : ''].filter(Boolean).join(' and ')
    : '';

  return (
    <div className={`${siteClass(s, undefined, 'guest')} rsvp-layer`} style={{ ...siteStyle(s), top: box.top, height: box.height }}>
      <div className="rsvp-backdrop" onClick={onClose} />
      <div className="rsvp-card" role="dialog" aria-modal="true" aria-labelledby="rsvp-title" ref={cardRef}>
        <button type="button" className="rsvp-x" onClick={onClose} aria-label="Close"><Icon name="x" size={22} /></button>
        <div className="rsvp-body" ref={bodyRef}>
          {done ? (
            <div className="rsvp-done" role="status">
              <span className="rsvp-done-ic"><Icon name="check" size={30} /></span>
              <h2 className="s-h" id="rsvp-title">Thank you, {done.first}</h2>
              <p>{done.yes ? `Your reply is in. We’ve noted ${party}, and we can’t wait to celebrate with you.` : 'We’ll miss you, and we’re grateful you let us know.'}</p>
              {!submit && <p className="rsvp-preview-note">This is a preview, so this reply wasn’t saved. Replies from your live website appear in your guest list.</p>}
              <div className="s-actions"><button type="button" className="s-btn" onClick={onClose}>Done</button></div>
              <p className="rsvp-again"><button type="button" onClick={() => { setDone(null); setRound((r) => r + 1); }}>Reply for someone else</button></p>
            </div>
          ) : (
            <>
              <p className="rsvp-kicker">{[h.name1, h.name2].filter(Boolean).join(' & ')}</p>
              <h2 className="s-h" id="rsvp-title">{b.title || 'RSVP'}</h2>
              <RsvpHead b={b} s={s} />
              {!submit && <p className="rsvp-preview-note">Preview: replies sent from here aren’t saved.</p>}
              <RsvpForm key={round} b={b} submit={submit} onDone={setDone} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RsvpForm({ b, submit, onDone }: { b: BlockOf<'rsvp'>; submit?: SubmitRsvp; onDone: (d: Done) => void }) {
  const d = b.data;
  const pre = 'rf' + b.id;
  const meals = String(d.meals || '').split('\n').map((x) => x.trim()).filter(Boolean);
  const askMeal = d.askMeal && meals.length > 0;
  const maxKids = Math.max(1, Math.min(10, parseInt(String(d.maxKids), 10) || 6));
  const [f, setF] = useState({ attending: '', plus: '', kidsYN: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const pick = (k: keyof typeof f, v: string) => { setF((o) => ({ ...o, [k]: v })); setErr(''); };
  const choice = (legend: string, name: keyof typeof f, a: [string, string], c: [string, string]) => (
    <fieldset className="s-field"><legend className="s-legend">{legend}</legend><div className="s-choice">
      {[a, c].map(([v, l]) => (
        <label key={v}><input type="radio" name={name} value={v} checked={f[name] === v} onChange={() => pick(name, v)} /><span>{l}</span></label>
      ))}
    </div></fieldset>
  );
  const mealSel = (name: string, id: string, label: string) => (
    <><label htmlFor={id}>{label}</label><select className="s-input" id={id} name={name} defaultValue=""><option value="">Choose a meal</option>{meals.map((m) => <option key={m}>{m}</option>)}</select></>
  );

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    const get = (k: string, max = 500) => String(fd.get(k) || '').trim().slice(0, max);
    const el = (n: string) => form.querySelector<HTMLElement>(`[name="${n}"]`);
    const fail = (msg: string, n?: string) => { setErr(msg); if (n) el(n)?.focus(); };
    const name = get('name').slice(0, 120);
    const yes = f.attending === 'yes';
    const plus = yes && f.plus === 'yes';
    const kidsYes = yes && f.kidsYN === 'yes';
    if (!name && !f.attending) return fail('Add your name and choose whether you’ll be joining.', 'name');
    if (!name) return fail('Add your name so the couple knows who replied.', 'name');
    if (!f.attending) return fail('Choose whether you’ll be joining.', 'attending');
    if (yes && d.allowPlusOne && !f.plus) return fail('Choose “Just me” or “Me + 1”.', 'plus');
    if (plus && !get('plusName')) return fail('Add your plus-one’s name, or choose “Just me”.', 'plusName');
    if (yes && d.askKids && !f.kidsYN) return fail('Let the couple know whether you’re bringing children.', 'kidsYN');
    const email = get('email').slice(0, 200);
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return fail('Check your email address, or leave it blank.', 'email');
    const kids = kidsYes ? Math.min(maxKids, Math.max(1, parseInt(get('kids') || '1', 10) || 1)) : 0;
    const adults = yes ? 1 + (plus ? 1 : 0) : 0;
    const reply = {
      name, email, attending: (yes ? 'yes' : 'no') as 'yes' | 'no',
      plusOne: plus, plusName: plus ? get('plusName').slice(0, 120) : '',
      kids, guests: adults,
      meal: yes ? get('meal').slice(0, 120) : '', plusMeal: plus ? get('plusMeal').slice(0, 120) : '',
      dietary: yes ? get('dietary') : '', song: yes ? get('song').slice(0, 200) : '',
      message: get('message', 1000),
    };
    if (submit) {
      setBusy(true);
      try { await submit(reply); }
      catch (x) { setBusy(false); return fail(errorMessage(x, 'Your reply couldn’t be sent. Check your connection and try again.')); }
      setBusy(false);
    }
    onDone({ first: name.split(/\s+/)[0], yes, adults, kids });
  }

  const cls = ['s-form', f.attending === 'yes' && 'is-yes', f.plus === 'yes' && 'has-plus', f.kidsYN === 'yes' && 'has-kids'].filter(Boolean).join(' ');
  return (
    <form className={cls} noValidate onSubmit={onSubmit} onInput={() => err && setErr('')} ref={formRef}>
      <div className="s-field"><label htmlFor={`${pre}-n`}>Your full name</label><input className="s-input" id={`${pre}-n`} name="name" autoComplete="name" maxLength={120} required /></div>
      <div className="s-field"><label htmlFor={`${pre}-e`}>Email</label><input className="s-input" id={`${pre}-e`} name="email" type="email" autoComplete="email" maxLength={200} /></div>
      {choice('Will you be joining us?', 'attending', ['yes', 'Joyfully accepts'], ['no', 'Regretfully declines'])}
      {d.allowPlusOne && <>
        <div className="only-yes">{choice('Are you coming as a couple or with a plus-one?', 'plus', ['no', 'Just me'], ['yes', 'Me + 1'])}</div>
        <div className="s-field only-plus"><label htmlFor={`${pre}-p`}>Their full name</label><input className="s-input" id={`${pre}-p`} name="plusName" autoComplete="off" maxLength={120} /></div>
      </>}
      {d.askKids && <>
        <div className="only-yes">{choice('Are you bringing children?', 'kidsYN', ['no', 'No'], ['yes', 'Yes'])}</div>
        <div className="s-field only-kids"><label htmlFor={`${pre}-k`}>How many children?</label><select className="s-input" id={`${pre}-k`} name="kids">{Array.from({ length: maxKids }, (_, i) => <option key={i}>{i + 1}</option>)}</select></div>
      </>}
      {askMeal && <div className="s-field only-yes">{mealSel('meal', `${pre}-m`, d.allowPlusOne ? 'Your meal' : 'Meal choice')}</div>}
      {askMeal && d.allowPlusOne && <div className="s-field only-plus">{mealSel('plusMeal', `${pre}-pm`, 'Their meal')}</div>}
      {d.askDietary && <div className="s-field only-yes"><label htmlFor={`${pre}-d`}>Dietary requirements</label><input className="s-input" id={`${pre}-d`} name="dietary" placeholder="For anyone in your party" maxLength={500} /></div>}
      {d.askSong && <div className="s-field only-yes"><label htmlFor={`${pre}-s`}>A song that will get you dancing</label><input className="s-input" id={`${pre}-s`} name="song" maxLength={200} /></div>}
      <div className="s-field"><label htmlFor={`${pre}-x`}>A note for the couple</label><textarea className="s-input" id={`${pre}-x`} name="message" rows={3} maxLength={1000} /></div>
      <p className="s-err" role="alert" hidden={!err}>{err}</p>
      <button className="s-btn" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send RSVP'}</button>
    </form>
  );
}
